import _ from 'lodash'
import React, { useContext, useState } from 'react'

import apis from 'browser/app/models/apis'
import ComponentsMap from 'browser/components'
import { ConfirmationModal } from 'browser/components/atomic-elements/organisms/confirmation-modal'
import { SheetContext } from 'browser/components/atomic-elements/atoms/sheet/sheet-manager'
import { AppNavigatorContext } from 'browser/contexts/app-navigator/app-navigator-context'
import { EntityFormSheet } from 'browser/components/atomic-elements/organisms/entity/entity-form-sheet'
import { CardHeader } from 'browser/components/atomic-elements/atoms/card/card-header'
import { CardHeaderItem } from 'browser/components/atomic-elements/atoms/card/card-header-item'
import { Button } from 'browser/components/atomic-elements/atoms/button/button'
import { HelpBlock } from 'browser/components/atomic-elements/atoms/help-block/help-block'
import { EntityRenderer } from 'shared-libs/components/entity/renderer'
import { Entity } from 'shared-libs/models/entity'
import { creationSourceFromWeb, copyToClipboard } from 'browser/app/utils/utils'

interface VerificationResult {
  verified: boolean
  message: string
}

export const SamlSettingsDetails: React.FC = (props) => {
  const { settings } = useContext(AppNavigatorContext)
  const { openOverlay } = useContext(SheetContext)
  const [forceUpdate, setForceUpdate] = useState(0)
  const [isLoading, setIsLoading] = useState(false)
  const [isVerifying, setIsVerifying] = useState(false)
  const [verificationError, setVerificationError] = useState<string | null>(null)
  const [errors, setErrors] = useState<unknown>([])
  const samlSettings: Entity = settings.getSamlSettings()
  const hasCustomSAMLSettings = settings.hasCustomSAMLSettings

  const store = apis.getStore()
  const entitySchema = store.getRecord('/1.0/entities/metadata/vendor_onelogin_saml_settings.json')

  const defaultValue = {
    creationSource: creationSourceFromWeb('Firm Saml Settings'),
  }

  const handleSamlCreate = () => {
    openOverlay(
      <EntityFormSheet
        entitySchema={entitySchema}
        defaultValue={defaultValue}
        onCreate={_.noop}
        size="md"
      />
    )
  }

  const handleSamlDelete = () => {
    const deleteEntity = () => {
      samlSettings.delete().then(() => {
        settings.removeSamlSettings()
        handleOnChange()
      })
    }
    ConfirmationModal.open({
      confirmationText: 'Do you want to delete this item?',
      confirmationTitle: 'Confirm Delete',
      modalDialogClassName: 'c-modal-dialog--sm',
      onPrimaryClicked: deleteEntity,
      primaryButtonText: 'Confirm',
    })
  }

  const renderSamlActionButton = () => {
    if (hasCustomSAMLSettings) {
      return null
    } else if (samlSettings) {
      return <Button buttonText="Delete SAML Settings" onClick={handleSamlDelete} />
    } else {
      return <Button buttonText="Create SAML Settings" onClick={handleSamlCreate} />
    }
  }

  const renderCustomSAMLWarning = () => {
    if (!hasCustomSAMLSettings) {
      return
    }
    return (
      <HelpBlock className="u-textCenter mh4 ph3 pv3">
        You have a custom SAML configuration. If you have any questions, please reach out to{' '}
        <a href="mailto:support@withvector.com">support@withvector.com</a>.
      </HelpBlock>
    )
  }

  const handleVerifyDomain = async () => {
    if (!samlSettings) return

    setIsVerifying(true)
    setVerificationError(null)

    try {
      const configId = samlSettings.get('uniqueId')
      const host = apis.getHost()
      const result = await apis.postJSON<VerificationResult>(
        `${host}/1.0/entities/actions/system/saml/verifyDomain/${configId}`,
        {}
      )

      if (result.verified) {
        // Refresh the entity to show updated status
        await samlSettings.reload()
        handleOnChange()
      } else {
        setVerificationError(result.message)
      }
    } catch (error: any) {
      const message =
        error?.errors?.[0]?.message || error?.message || 'Verification failed. Please try again.'
      setVerificationError(message)
    } finally {
      setIsVerifying(false)
    }
  }

  const renderVerificationStatus = () => {
    if (!samlSettings) return null
    if (hasCustomSAMLSettings) return null

    const domainValidated = samlSettings.get('core_integrations_saml_config.domainValidated')
    const verificationCode = samlSettings.get('core_integrations_saml_config.verificationCode')
    const emailDomain = samlSettings.get('core_integrations_saml_config.emailDomain')

    if (!emailDomain) return null

    if (domainValidated) {
      return (
        <HelpBlock
          className="u-textCenter mh4 ph3 pv3"
          style={{ backgroundColor: '#d4edda', borderColor: '#c3e6cb', color: 'black' }}
        >
          <strong>Domain verified.</strong> SAML authentication is active for &quot;{emailDomain}
          &quot;.
        </HelpBlock>
      )
    }

    return (
      <div
        className="mh4 ph3 pv3"
        style={{ backgroundColor: '#fff3cd', border: '1px solid #ffeeba', borderRadius: '4px' }}
      >
        <HelpBlock
          className="mb3"
          style={{ backgroundColor: 'transparent', border: 'none', padding: 0, color: 'black' }}
        >
          <strong>Domain verification required.</strong> SAML authentication will not work until the
          domain &quot;{emailDomain}&quot; is verified.
        </HelpBlock>

        {verificationCode && (
          <>
            <p className="mb2">
              Add a TXT record with the following content to your DNS configuration:
            </p>
            <div
              className="flex items-center mb3"
              style={{
                backgroundColor: '#f8f9fa',
                padding: '8px 12px',
                borderRadius: '4px',
                fontFamily: 'monospace',
                fontSize: '12px',
                wordBreak: 'break-all',
              }}
            >
              <span className="flex-grow-1">{verificationCode}</span>
              <Button
                buttonText="Copy"
                className="ml2"
                size="sm"
                onClick={() => copyToClipboard(verificationCode)}
              />
            </div>
            <p className="mb3">
              DNS changes may take up to 48 hours to propagate. Once added, click &quot;Verify
              Now&quot; to check.
            </p>
          </>
        )}

        {verificationError && (
          <HelpBlock
            className="mb3"
            style={{ backgroundColor: '#f8d7da', borderColor: '#f5c6cb', color: 'black' }}
          >
            {verificationError}
          </HelpBlock>
        )}

        <Button
          buttonText={isVerifying ? 'Verifying...' : 'Verify Now'}
          onClick={handleVerifyDomain}
          isDisabled={isVerifying || !verificationCode}
        />
      </div>
    )
  }

  const handleRollback = () => {
    samlSettings.rollback()
    setErrors([])
  }

  const handleSave = () => {
    setIsLoading(true)
    samlSettings
      .save()
      .then(() => {
        setIsLoading(false)
      })
      .catch(({ errors }: { errors?: unknown }) => {
        setIsLoading(false)
        setErrors(errors)
      })
  }

  const handleOnChange = () => {
    setForceUpdate(forceUpdate + 1)
  }

  let content = <div className="grid-block align-verticalCenter u-justifyContentCenter"></div>

  if (samlSettings) {
    content = (
      <EntityRenderer
        isEditable={!hasCustomSAMLSettings}
        applyDefaults={true}
        actions={{
          handleRollback,
          handleSave,
        }}
        componentsMap={ComponentsMap}
        errors={errors}
        onChangeComplete={handleOnChange}
        state={{ errors, isLoading }}
        uiSchemaPath={'uiSchema.web.entityDetailView'}
        value={samlSettings}
      />
    )
  }

  return (
    <React.Fragment>
      <CardHeader className="c-cardHeader--nonSeparating u-borderBottom">
        <CardHeaderItem
          className="c-cardHeader-item--grow c-cardHeader-item--left"
          title="SAML Settings"
        />
        <CardHeaderItem>{renderSamlActionButton()}</CardHeaderItem>
      </CardHeader>
      {renderCustomSAMLWarning()}
      {renderVerificationStatus()}
      {content}
    </React.Fragment>
  )
}
